import React, { useEffect, useState } from 'react';
import { Stethoscope, BarChart, Check, AlertCircle, XCircle, User, Heart, Sparkles, Quote, TrendingUp, TrendingDown } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useSelector } from 'react-redux';
import CommonLoader from '../CommonLoader';
import { useTranslation } from 'react-i18next';
import { getFileURL } from '../../utils/fileURL';
import { formatTime } from '../../utils/clock';

// Presentational — booking analytics arrive via props from the patient dashboard
// socket; the page owns the range control (Week/Month/Year). Props are aliased to
// the original local names to keep the rest of the component untouched.
const PatientWelcomeSection = ({ booking: bookingData, loading: patientBookingAnalyticsLoading, selectedRange = 'Week', onRangeChange }) => {
    const { t } = useTranslation('dashboard');
    const [currentTime, setCurrentTime] = useState(new Date());
    const [imageError, setImageError] = useState(false);
    const { selectedPatient, isLoading, error } = useSelector((state) => state.patient);

    useEffect(() => {
        const interval = setInterval(() => setCurrentTime(new Date()), 1000);
        return () => clearInterval(interval);
    }, []);

    // Reset image error when patient changes
    useEffect(() => {
        setImageError(false);
    }, [selectedPatient]);


    const formattedDate = currentTime.toLocaleDateString('en-GB', {
        weekday: 'long',
        day: 'numeric',
        month: 'long',
        year: 'numeric',
    });
    // The one clock format, "2:30 PM" (utils/clock).
    const formattedTime = formatTime(currentTime);

    // Get time-based greeting
    const getTimeGreeting = () => {
        const hour = currentTime.getHours();
        if (hour < 12) return t('dashboard.greetings.good_morning');
        if (hour < 17) return t('dashboard.greetings.good_afternoon');
        return t('dashboard.greetings.good_evening');
    };

    // Welcome quotes array
    const welcomeQuotes = [
        t('dashboard.quote_1', 'Your health is your greatest wealth!'),
        t('dashboard.quote_2', 'Every step towards wellness is a victory!'),
        t('dashboard.quote_3', 'Taking care of yourself is the best investment!'),
        t('dashboard.quote_4', 'Your journey to better health starts today!'),
        t('dashboard.quote_5', 'Small steps lead to big changes!'),
        t('dashboard.quote_6', "You're stronger than you think!")
    ];

    // Get random quote
    const [currentQuote, setCurrentQuote] = useState(welcomeQuotes[0]);

    useEffect(() => {
        const randomQuote = welcomeQuotes[Math.floor(Math.random() * welcomeQuotes.length)];
        setCurrentQuote(randomQuote);
    }, []);

    const cards = [
        { id: 'total', label: t('dashboard.analytics_total'), icon: BarChart, bg: 'bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B]' },
        { id: 'Completed', label: t('dashboard.analytics_completed'), icon: Check, bg: 'bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B]' },
        { id: 'MissedOut', label: t('dashboard.analytics_missed_out'), icon: AlertCircle, bg: 'bg-gradient-to-r from-[#6B7280] to-[#4B5563]' },
        { id: 'Canceled', label: t('dashboard.analytics_canceled'), icon: XCircle, bg: 'bg-gradient-to-r from-[#9CA3AF] to-[#6B7280]' },
    ];

    // Get booking data from API
    const getBookingCounts = () => {
        if (!bookingData) {
            return {
                total: 0,
                Completed: 0,
                MissedOut: 0,
                Canceled: 0
            };
        }
        return {
            total: bookingData.total || 0,
            Completed: bookingData.counts?.Completed || 0,
            MissedOut: bookingData.counts?.NoShow || 0,
            Canceled: bookingData.counts?.Canceled || 0
        };
    };

    const handleRangeChange = (range) => {
        onRangeChange?.(range);
    };

    return (
        <div
            data-testid="welcome-section"
            className="grid grid-cols-1 gap-4 sm:gap-4 w-full lg:grid-cols-2 xl:grid-cols-[minmax(0,0.4fr)_minmax(0,1.2fr)]"
        >
            {/* Left Section: Patients Info and Welcome */}
            <motion.div
                data-testid="welcome-left-section"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                className="relative bg-gradient-to-br from-[#328e8e] to-[#4ABDBD] rounded-xl p-4 sm:p-6 md:p-8 shadow-md backdrop-blur-sm overflow-hidden  flex flex-col justify-center"
            >
                {/* Background decorative elements */}
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/20 rounded-full -translate-y-16 translate-x-16"></div>
                <div className="absolute bottom-0 left-0 w-24 h-24 bg-white/10 rounded-full translate-y-12 -translate-x-12"></div>
                <div className="absolute top-1/2 right-1/4 w-16 h-16 bg-white/5 rounded-full"></div>

                {/* Sparkle effects */}
                <motion.div
                    data-testid="welcome-sparkles"
                    animate={{ rotate: 360 }}
                    transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
                    className="absolute top-4 right-4"
                >
                    <Sparkles data-testid="welcome-sparkles-icon" className="w-6 h-6 text-white/40" />
                </motion.div>

                <div data-testid="welcome-content" className="relative z-10 space-y-4 sm:space-y-6">
                    {/* Header with greeting and time */}
                    <motion.div
                        data-testid="welcome-header"
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.2, duration: 0.5 }}
                        className="text-center"
                    >
                        <div data-testid="welcome-greeting-row" className="flex items-center justify-center gap-2 mb-2">
                            <Heart data-testid="welcome-greeting-icon" className="w-4 h-4 sm:w-5 sm:h-5 text-white/80" />
                            <p data-testid="welcome-greeting-text" className="text-white/90 text-xs sm:text-sm font-medium">
                                {getTimeGreeting()}
                            </p>
                        </div>
                        <h1 data-testid="welcome-back-title" className="text-xl sm:text-2xl md:text-3xl font-bold text-white">
                            {t('dashboard.welcome_back')}
                        </h1>
                    </motion.div>

                    {/* Patient info section */}
                    <motion.div
                        data-testid="patients-info-section"
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.4, duration: 0.5 }}
                        className="flex flex-col items-center space-y-4"
                    >
                        <div
                            data-testid="patients-avatar-container"
                            className="relative"
                        >
                            <div
                                data-testid="patients-avatar"
                                className="relative w-16 h-16 sm:w-20 sm:h-20 md:w-24 md:h-24 rounded-full border-4 border-white/30 shadow-xl"
                            >
                                {(selectedPatient?.image && selectedPatient.image.trim() !== '' && !imageError) ? (
                                    <img
                                        data-testid="patients-image"
                                        src={getFileURL(selectedPatient.image)}
                                        alt={selectedPatient?.name || t('dashboard.patient_alt', 'Patient')}
                                        className="w-full h-full object-cover rounded-full"
                                        onError={() => setImageError(true)}
                                    />
                                ) : (
                                    <div
                                        data-testid="patients-initial"
                                        className="w-full h-full flex items-center justify-center bg-gradient-to-br from-white to-white/90 rounded-full text-[#4ABDBD] font-bold text-2xl sm:text-3xl md:text-4xl"
                                    >
                                        {selectedPatient?.name?.charAt(0)?.toUpperCase() || 'P'}
                                    </div>
                                )}
                                <div
                                    data-testid="stethoscope-badge"
                                    className="absolute -bottom-2 -right-2 bg-white rounded-full p-1.5 sm:p-2 border-2 border-[#4ABDBD] shadow-lg"
                                >
                                    <Heart
                                        data-testid="stethoscope-icon"
                                        className="w-3 h-3 sm:w-4 sm:h-4 text-[#4ABDBD]"
                                        aria-hidden="true"
                                    />
                                </div>
                            </div>
                        </div>

                        <div
                            data-testid="patients-details"
                            className="text-center"
                        >
                            <h2
                                data-testid="patients-name"
                                className="text-lg sm:text-xl md:text-2xl font-bold text-white"
                            >
                                {selectedPatient?.name}
                            </h2>
                        </div>
                    </motion.div>

                    {/* Inspirational quote */}
                    {/* <motion.div
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.6, duration: 0.5 }}
                        className="bg-white/10 backdrop-blur-sm rounded-xl p-4 border border-white/20"
                    >
                        <div className="flex items-start gap-3">
                            <Quote className="w-5 h-5 text-white/60 mt-1 flex-shrink-0" />
                            <p className="text-white/90 text-sm sm:text-base font-medium italic leading-relaxed">
                                {currentQuote}
                            </p>
                        </div>
                    </motion.div> */}
                </div>
            </motion.div>

            {/* Right Section: Booking Analytics */}
            <motion.div
                data-testid="patient-booking-analytics"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="bg-white rounded-xl p-4 sm:p-6 border border-gray-200/50 shadow-md backdrop-blur-sm relative flex flex-col"
            >
                <div data-testid="patient-booking-analytics-header" className="flex items-center justify-between mb-4 sm:mb-6 flex-shrink-0">
                    <h3 data-testid="patient-booking-analytics-title" className="text-base sm:text-lg font-bold text-gray-900 flex items-center">
                        <BarChart
                            data-testid="patient-booking-analytics-icon"
                            className="w-4 h-4 sm:w-5 sm:h-5 mr-2 sm:mr-3 text-[#4ABDBD]"
                            aria-hidden="true"
                        />
                        <span data-testid="patient-booking-analytics-title-xs-up" className="hidden xs:inline">{t('dashboard.booking_analytics')}</span>
                        <span data-testid="patient-booking-analytics-title-xs-down" className="xs:hidden">{t('dashboard.booking_analytics')}</span>
                        {/* <span className="text-[10px] sm:text-xs font-semibold text-[#4ABDBD] bg-[#4ABDBD]/10 px-2 py-1 rounded-full whitespace-nowrap ml-3">
                            {t(`dashboard.time_ranges.${selectedRange.toLowerCase()}`)}
                        </span> */}
                    </h3>
                </div>

                {/* Time Range Tabs */}
                <div data-testid="patient-range-tabs" className="flex space-x-1 mb-4 sm:mb-6 bg-gradient-to-r from-gray-100 to-gray-200 p-1 rounded-lg">
                    {['Week', 'Month', 'Year'].map((range) => (
                        <button
                            key={range}
                            data-testid={`patient-range-tab-${range.toLowerCase()}`}
                            onClick={() => handleRangeChange(range)}
                            className={`relative flex-1 px-2 sm:px-4 py-2 text-xs sm:text-sm font-semibold rounded-md transition-all duration-300 ${selectedRange === range
                                ? 'text-white'
                                : 'text-gray-600 hover:text-[#4ABDBD] cursor-pointer'
                                }`}
                        >
                            {selectedRange === range && (
                                <motion.div
                                    data-testid={`patient-range-tab-active-${range.toLowerCase()}`}
                                    layoutId="activePatientWelcomeTab"
                                    className="absolute inset-0 bg-gradient-to-r from-[#4ABDBD] to-[#3A9B9B] rounded-md shadow-md !m-0"
                                    transition={{ type: 'spring', bounce: 0.2, duration: 0.6 }}
                                />
                            )}
                            <span data-testid={`patient-range-tab-label-${range.toLowerCase()}`} className="relative z-20">{t(`dashboard.time_ranges.${range.toLowerCase()}`)}</span>
                        </button>
                    ))}
                </div>
                <AnimatePresence>
                    {patientBookingAnalyticsLoading ? (
                        <div data-testid="patient-booking-analytics-loading" className="flex flex-col items-center justify-center py-12 flex-1">
                            <CommonLoader />
                            <span data-testid="patient-booking-analytics-loading-text" className="text-sm text-gray-600 mt-2">{t('dashboard.loading_analytics')}</span>
                        </div>
                    ) : (
                        <motion.div
                            data-testid="patient-booking-analytics-cards"
                            initial={{ opacity: 0, y: 10 }}
                            animate={{ opacity: 1, y: 0 }}
                            exit={{ opacity: 0, y: -10 }}
                            transition={{ duration: 0.3 }}
                            className="grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3 md:gap-4 flex-1"
                        >
                            {cards.map((card, index) => {
                                const counts = getBookingCounts();
                                const count = counts[card.id] || 0;
                                const cardKey = card.id.toLowerCase();

                                return (
                                    <motion.div
                                        key={card.id}
                                        data-testid={`patient-booking-analytics-card-${cardKey}`}
                                        whileHover={{ scale: 1.02, y: -2 }}
                                        whileTap={{ scale: 0.98 }}
                                        className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-2.5 sm:p-3 md:p-4 lg:p-5 border border-gray-200/50 shadow-sm hover:shadow-lg transition-all duration-300 flex flex-col justify-center w-full"
                                        aria-label={`${card.label}: ${count.toLocaleString()} ${t('dashboard.appointments', 'appointments')}`}
                                    >
                                        <div className="flex flex-col items-center text-center space-y-1.5 sm:space-y-2 md:space-y-3">
                                            <div
                                                data-testid={`patient-booking-analytics-card-${cardKey}-icon-wrap`}
                                                className={`p-1.5 sm:p-2 md:p-3 rounded-full shadow-lg ${card.bg}`}
                                            >
                                                <card.icon
                                                    data-testid={`patient-booking-analytics-card-${cardKey}-icon`}
                                                    className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 lg:w-6 lg:h-6 text-white"
                                                    aria-hidden="true"
                                                />
                                            </div>
                                            <div className="space-y-0.5 sm:space-y-1">
                                                <p data-testid={`patient-booking-analytics-card-${cardKey}-value`} className="text-sm sm:text-base md:text-lg lg:text-xl xl:text-2xl font-bold text-gray-900">
                                                    {count.toLocaleString()}
                                                </p>
                                                <p data-testid={`patient-booking-analytics-card-${cardKey}-label`} className="text-[0.6rem] sm:text-[0.65rem] md:text-xs lg:text-sm text-gray-600 font-medium">
                                                    {card.label}
                                                </p>
                                            </div>
                                        </div>
                                    </motion.div>
                                );
                            })}
                        </motion.div>
                    )}
                </AnimatePresence>
            </motion.div>
        </div>
    );
};

export default PatientWelcomeSection;